import CustomsPro360 from 'assets/images/customsPro-360.svg';
import CustomsProENS from 'assets/images/customsPro-ENS.svg';

import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';

import { Box, Grid, Paper, SvgIcon, Typography } from '@mui/material';

import { Loading } from '@/components';
import { CreateNewRequestModal } from '@/features/customsPass/createNewRequest';
import { OPEN_CUSTOMS_AND_CROSSING } from '@/features/shared/helpers/typologies';
import { BoxChoice } from '@/features/sherpass/servicesOffer/boxChoice';
import { BoxLineIcon, TruckIcon } from '@/utils/iconSVG';

import {
  stylesBoxContainer,
  stylesServicesOffer,
  stylesBoxChoiceIcon,
  stylesBoxContainerTitle,
  stylesBoxContainerSubtitle,
} from './ServicesOffer.styles';
import { useServicesOffer } from './hooks';

export const ServicesOffer: React.FunctionComponent = () => {
  const { t } = useTranslation('customsPass_servicesOffer');

  const {
    isGetFolderTypologiesByBorderCrossingServiceResponseLoading,
    folderTypologiesByBorderCrossingServiceResponse,
    isCommercialOfferAccessManagementLoading,
    isCommercialOfferAccessManagementEnabled,
    isOpenCreateNewRequestModal,
    isGetClientAccessLoading,
    clientAccess,
    handleOpenCreateNewRequestModal,
    handleCloseCreateNewRequestModal,
    handleCreateNewBorderCrossingRoroRequest,
  } = useServicesOffer();

  if (
    isGetClientAccessLoading ||
    isCommercialOfferAccessManagementLoading ||
    isGetFolderTypologiesByBorderCrossingServiceResponseLoading
  ) {
    return <Loading />;
  }
  const customsPassDescriptionTranslationKey = folderTypologiesByBorderCrossingServiceResponse.get(
    OPEN_CUSTOMS_AND_CROSSING.CUSTOMS_PASS,
  );
  const borderCrossingRORODescriptionTranslationKey = folderTypologiesByBorderCrossingServiceResponse.get(
    OPEN_CUSTOMS_AND_CROSSING.BORDER_CROSSING_RORO,
  );

  let isCustomsPassEnabled = true;
  let isBorderCrossingRoRoEnabled = true;
  if (isCommercialOfferAccessManagementEnabled) {
    isCustomsPassEnabled = Boolean(clientAccess.commercialOfferAccess.has(OPEN_CUSTOMS_AND_CROSSING.CUSTOMS_PASS));

    isBorderCrossingRoRoEnabled = Boolean(
      clientAccess.commercialOfferAccess.has(OPEN_CUSTOMS_AND_CROSSING.BORDER_CROSSING_RORO),
    );
  }

  return (
    <Box sx={stylesServicesOffer}>
      <Box component={Paper} elevation={4} sx={stylesBoxContainer}>
        <Typography variant='h1' sx={stylesBoxContainerTitle}>
          {t('customsPass.servicesOffer.title')}
        </Typography>
        <Typography variant='h2' sx={stylesBoxContainerSubtitle}>
          {t('customsPass.servicesOffer.servicesType')}
        </Typography>

        <Grid container spacing={2} sx={{ overflow: 'scroll', 'overflow-x': 'auto', 'overflow-y': 'auto' }}>
          {customsPassDescriptionTranslationKey && (
            <Grid item xs={12} sm={12} md={12} lg={6}>
              <BoxChoice
                disabled={!isCustomsPassEnabled}
                heading={
                  <>
                    <Box component='img' width={250} height={45} src={CustomsPro360} />
                    <Box component='span'>
                      <SvgIcon
                        component={BoxLineIcon}
                        color='info'
                        data-testid='svg-icon'
                        sx={{ ...stylesBoxChoiceIcon }}
                      />
                    </Box>
                  </>
                }
                items={[
                  <ReactMarkdown>
                    {t(`customsPass.servicesOffer.servicesType.${customsPassDescriptionTranslationKey}.content`)}
                  </ReactMarkdown>,
                ]}
                onSelectAnOffer={handleOpenCreateNewRequestModal(OPEN_CUSTOMS_AND_CROSSING.CUSTOMS_PASS)}
              />
            </Grid>
          )}

          {borderCrossingRORODescriptionTranslationKey && (
            <Grid item xs={12} sm={12} md={12} lg={6}>
              <BoxChoice
                disabled={!isBorderCrossingRoRoEnabled}
                key={OPEN_CUSTOMS_AND_CROSSING.BORDER_CROSSING_RORO}
                heading={
                  <>
                    <Box component='img' width={250} height={45} src={CustomsProENS} />
                    <Box component='span'>
                      <SvgIcon color='info' component={TruckIcon} data-testid='svg-icon' sx={stylesBoxChoiceIcon} />
                    </Box>
                  </>
                }
                items={[
                  <ReactMarkdown>
                    {t(`customsPass.servicesOffer.servicesType.${borderCrossingRORODescriptionTranslationKey}.content`)}
                  </ReactMarkdown>,
                ]}
                onSelectAnOffer={handleCreateNewBorderCrossingRoroRequest}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      {isOpenCreateNewRequestModal && (
        <CreateNewRequestModal isOpen onCloseCreateNewRequestModal={handleCloseCreateNewRequestModal} />
      )}
    </Box>
  );
};
